import { useMemo } from 'react';
import { t } from '@lingui/macro';
import _ from 'lodash';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { useTableSeries } from '../../charts/performance-chart/funcs/UseTableSeries';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { hypervisorFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Hypervisor CPU Usage: Top 5 VMs`;

const baseDataSource = {
    url: '/query/table',
    params: {
        type: 'server',
        subType: 'virtualmachine',
        host: true,
        rowStart: 0,
        rowLimit: 5,
    },
};

const PERCENT = 'PERCENT';
const FREQUENCY = 'FREQUENCY';

const typeConfig = {
    [PERCENT]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (%)`,
                },
                ceiling: 100,
            },
        ],
        chartType: 'areaspline',
        stacking: 'normal',
        tooltip: BuiltInTooltip,
        showGranularity: true,
        transforms: {
            syncAxisPoints: true,
        },
    },
    [FREQUENCY]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (Hz)`,
                },
                ceiling: undefined,
            },
        ],
        chartType: 'areaspline',
        stacking: 'normal',
        tooltip: BuiltInTooltip,
        showGranularity: true,
        transforms: {
            syncAxisPoints: true,
        },
    },
};

const baseTrend = {
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        units: '%',
        chartType: 'areaspline',
        key: 'vmCpuPctUtil',
        multiplier: 1,
        forceDecimals: true,
    },
    [FREQUENCY]: {
        formatter: FORMATTER_TYPES.LOG,
        units: 'Hz',
        chartType: 'areaspline',
        key: 'vmCpuUsage',
        multiplier: 1000000,
        forceDecimals: true,
    },
};

function useDataSource(type) {
    return useMemo(() => {
        const metric = type === PERCENT ? 'vmCpuPctUtil' : 'vmCpuUsage';
        return _.merge({}, baseDataSource, { params: { sort: `-${metric}`, metrics: [metric], trends: [metric] } });
    }, [type]);
}

function useChartConfig(type, data) {
    const chartTrends = useTableSeries(baseTrend[type], data, {
        nameMapping: 'server.name',
        colors: ['#7E57C2', '#0071BC', '#91BED4', '#FFBC67', '#DA727E'],
    });

    return useMemo(() => {
        if (!chartTrends) return null;
        return {
            ...typeConfig[type],
            chartTrends,
        };
    }, [type, chartTrends]);
}

const selections = [
    { value: PERCENT, label: '%' },
    { value: FREQUENCY, label: t`Frequency` },
];

function HypervisorCpuUtilizationPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const dataSource = useDataSource(chartDisplayType);
    const data = usePanelDataService(dataSource);
    const chartData = data?.data; // Table data iterates over multiple rows (one per series)
    const chartConfig = useChartConfig(chartDisplayType, chartData);

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

HypervisorCpuUtilizationPanel.propTypes = panelPropTypes;

HypervisorCpuUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareHostCpuBreakdownTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.HYPERVISOR,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`CPU utilization trend for top 5 VMs on a selected hypervisor`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, hypervisorFilterId],
        optional: [],
    },
})(HypervisorCpuUtilizationPanel);
